import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { createNote } from "api";
import { Modal, FormField, Spinner } from "components/common";
import type { ScheduleItem } from "types";

export function AddNoteModal({
  item,
  partnerName,
  onClose,
  onSuccess,
}: {
  item:        ScheduleItem;
  partnerName: string;
  onClose:     () => void;
  onSuccess:   () => void;
}) {
  const [note, setNote] = useState("");
  const [noteType, setNoteType] = useState<"item" | "partner">("item");

  const { mutate, isPending, error } = useMutation({
    mutationFn: () => createNote(
      item.partner_entity_id,
      note.trim(),
      noteType === "item" ? item.id : undefined
    ),
    onSuccess,
  });

  return (
    <Modal title={`Add note — ${partnerName}`} onClose={onClose}>
      <div className="space-y-4">
        <FormField label="Note type">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden w-fit">
            <button
              type="button"
              onClick={() => setNoteType("item")}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                noteType === "item" ? "bg-blue-600 text-white" : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              Item note
            </button>
            <button
              type="button"
              onClick={() => setNoteType("partner")}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                noteType === "partner" ? "bg-amber-600 text-white" : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              Partner note
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {noteType === "item"
              ? "This note will be attached to this specific pickup."
              : "This note will be attached to the partner and visible on all their pickups."}
          </p>
        </FormField>
        <FormField label="Note">
          <textarea
            className="input min-h-[80px]"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Enter a note…"
            autoFocus
          />
        </FormField>
        {error && <p className="text-sm text-red-500">Something went wrong.</p>}
        <div className="flex gap-3 pt-1">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!note.trim() || isPending}
            onClick={() => mutate()}
          >
            {isPending ? <Spinner className="h-4" /> : "Save note"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
